// Archivo: src/features/subscription/components/ManualPaymentForm.tsx
// VERSIÓN v18 - Sin campo referencia, soporta PDF

import React, { useState, useEffect } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { useError } from '../../../context/ErrorContext';
import { useAuth } from '../../../context/AuthContext';
import { billingApi } from '../../../api/billingApi';
import { useWaCheck, waBadge } from '../../../hooks/useWaCheck';
import ImageUpload from '../../../components/common/ImageUpload';

interface BankAccountInfo {
  bank?: string;
  accountName?: string;
  accountNumber?: string;
  clabe?: string;
  beneficiaryId?: string;
  bankBranch?: string;
}

export interface ManualPaymentSubmitData {
  amount: number;
  method: 'manual';
  email?: string;
  whatsapp?: string;
  receiptImageUrl: string;
}

interface ManualPaymentFormProps {
  onSubmit: (data: ManualPaymentSubmitData) => Promise<void>;
  isSubmitting: boolean;
  amount: number;
  currency?: string;
}

const ManualPaymentForm: React.FC<ManualPaymentFormProps> = ({
  onSubmit,
  isSubmitting,
  amount,
  currency = "MXN"
}) => {
  const { addValidationError, clearErrors } = useError();

  const { currentUser } = useAuth();
  const [email, setEmail] = useState('');
  // Precargado con el teléfono del perfil: si el dueño lo corrige aquí y el nuevo
  // valida con WhatsApp, el backend lo promueve a su perfil (waCheck.reconcilePayerPhone).
  const [whatsapp, setWhatsapp] = useState(() => currentUser?.phoneNumber || '');
  const wa = useWaCheck();
  const [receiptImageUrl, setReceiptImageUrl] = useState<string>('');
  // Sin datos falsos de respaldo: si el endpoint no trae datos, se muestra un aviso
  // (mostrar una CLABE/cuenta inventada haría que el cliente transfiera a la nada).
  const [bankAccount, setBankAccount] = useState<BankAccountInfo>({});
  const [loadingBankInfo, setLoadingBankInfo] = useState(true);

  useEffect(() => {
    const fetchBankAccountInfo = async () => {
      try {
        setLoadingBankInfo(true);
        const { bankAccount: bankData } = await billingApi.getPaymentMethods();
        if (bankData) {
          setBankAccount({
            bank: bankData.bank,
            accountName: bankData.accountName,
            accountNumber: bankData.accountNumber,
            clabe: bankData.clabe,
            beneficiaryId: bankData.beneficiaryId,
            bankBranch: bankData.bankBranch
          });
        }
      } catch (error) {
        console.error('Error fetching bank info:', error);
      } finally {
        setLoadingBankInfo(false);
      }
    };
    fetchBankAccountInfo();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();

    if (!receiptImageUrl) {
      addValidationError('Se requiere el comprobante de pago', 'manual-payment-file-missing');
      return;
    }

    const paymentData: ManualPaymentSubmitData = {
      amount,
      method: 'manual',
      receiptImageUrl,
      ...(email.trim() && { email: email.trim() }),
      ...(whatsapp.trim() && { whatsapp: whatsapp.trim() })
    };

    await onSubmit(paymentData);
  };

  if (loadingBankInfo) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
      {/* Información Bancaria */}
      <div className="bg-blue-50 dark:bg-blue-500/10 border-l-4 border-blue-400 p-3 sm:p-4 rounded-md">
        <div className="flex flex-col sm:flex-row sm:items-start">
          <div className="flex-shrink-0 mb-2 sm:mb-0 sm:mr-3">
            <Info className="h-5 w-5 text-blue-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">Información de pago</h3>
            <div className="mt-2 text-xs sm:text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium">Realiza una transferencia bancaria con los siguientes datos:</p>
              {(bankAccount.clabe || bankAccount.bank) ? (
                <ul className="mt-2 space-y-1 list-disc list-inside pl-0 sm:pl-2">
                  {bankAccount.bank && <li><span className="font-medium">Banco:</span> {bankAccount.bank}</li>}
                  {bankAccount.accountName && <li><span className="font-medium">Beneficiario:</span> {bankAccount.accountName}</li>}
                  {bankAccount.accountNumber && <li><span className="font-medium">Cuenta:</span> {bankAccount.accountNumber}</li>}
                  {bankAccount.clabe && <li><span className="font-medium">CLABE:</span> {bankAccount.clabe}</li>}
                  {bankAccount.beneficiaryId && (
                    <li><span className="font-medium">RFC:</span> {bankAccount.beneficiaryId}</li>
                  )}
                  {bankAccount.bankBranch && (
                    <li><span className="font-medium">Sucursal:</span> {bankAccount.bankBranch}</li>
                  )}
                  <li><span className="font-medium">Monto:</span> {currency} ${amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</li>
                </ul>
              ) : (
                <p className="mt-2 font-medium text-amber-700 dark:text-amber-300">
                  No pudimos cargar los datos bancarios. Por favor contacta a soporte antes de transferir.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-content-2">
          Correo electrónico (Opcional)
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          placeholder="tu@correo.com"
        />
        <p className="mt-1 text-xs text-content-3">Lo usaremos para contactarte si hay alguna duda.</p>
      </div>

      {/* WhatsApp */}
      <div>
        <label htmlFor="whatsapp" className="block text-sm font-medium text-content-2">
          Número de WhatsApp (Opcional)
        </label>
        <input
          type="tel"
          id="whatsapp"
          value={whatsapp}
          onChange={(e) => { setWhatsapp(e.target.value); wa.reset(); }}
          onBlur={() => wa.check(whatsapp)}
          className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          placeholder="Ej. 55 1234 5678"
        />
        {(() => { const b = waBadge(wa.state); return b
          ? <p className={`mt-1 text-xs ${b.className}`}>{b.text}</p>
          : <p className="mt-1 text-xs text-content-3">Para aclaraciones rápidas si es necesario.</p>; })()}
      </div>

      {/* Comprobante */}
      <ImageUpload
        title="Comprobante de pago *"
        description="Sube una foto o captura de tu comprobante (JPG, PNG o WEBP hasta 5MB)"
        accept="image/jpeg,image/png,image/webp"
        maxSizeMB={5}
        onImageChange={(url) => {
          setReceiptImageUrl(url);
          if (url) clearErrors();
        }}
      />

      {/* Submit */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          disabled={isSubmitting || !receiptImageUrl}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: 'rgb(var(--c-accent))', boxShadow: `0 2px 4px 0 rgb(var(--c-accent) / 0.31)` }}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
              Procesando...
            </>
          ) : (
            'Enviar Comprobante'
          )}
        </button>
      </div>
    </form>
  );
};

export default ManualPaymentForm;